import { Injectable } from '@angular/core';
import { EMPTY_STRING } from '@modeso/aumico-lib-core-fe';
import {
    IAccountMappingTemplate,
    IAccountMappingTemplateGroupItemFLI,
    IAnnualReport,
    IEquity,
    IFinancialLineItemComponent,
    IFreeTable,
    IMovementScheduleComponent,
    INotesComponent,
    INotesInputComponent,
    INotesSection,
    INotesSelectComponent,
    ISuperTableComponent,
    Notes,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue, INameId } from '@modeso/types__aumico-lib-core-be';
import {
    ComponentType,
    ComponentsOptionsDTO,
    IEquityOptions,
    IFinancialLineItemOptions,
    IFreeTableOptions,
    IInputOptions,
    IMovementScheduleEditableColumnHeaders,
    IRichText,
    ISelectOptions,
    ISuperTableOptions,
    NotesTypeOfNotesTemplateDTO,
    Options,
    SuperTableColumnType,
} from '@modeso/types__aumico-lib-notes-be';
import { IInputDataType } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/input-type.enum';
import { SuperTableColumnBelongingYear } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/super_table_column_belonging_year.enum';
import { cloneDeep } from 'lodash-es';
import { IAnnualReportAccordionsData } from '../interfaces/IAnnualReportAccordionsData.interface';
import { IAnnualReportDefaultSettings } from '../interfaces/IAnnualReportDefaultSettings.interface';
import { INotesAccordion } from '../interfaces/notes-accordion.interface';
import { MovementScheduleHelper } from './movementSchedule.helper';
import {
    findFlis,
    getAllFlis,
    getSuperTableCopiedRows,
    getTotalAndRows,
    mapFlis,
    mapFlisForMovement,
} from './notesCopy.helper';
import { NotesTypesHelper } from './notesTypes.helper';

const SINGLE_ELEMENT_COMPONENT_OPTIONS = 0;



@Injectable({
    providedIn: 'root',
})
export class NotesHelper {
    constructor(
        private notesTypesHelper: NotesTypesHelper,
        private readonly movementScheduleHelper: MovementScheduleHelper,
    ) { }

    public static generateComponentId(componentName: string): string {
        return `${componentName}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }

    public static compareISelectionObjects(firstObject: INameId, secondObject: INameId): boolean {
        const keys: string[] = Object.keys(secondObject);

        for (const key of keys) {
            if (firstObject[key] !== secondObject[key]) {
                return false;
            }
        }

        return true;
    }
    public prepareOutputtedAccordionsData(
        change: Notes,
        index: number,
        componentIndex: number,
        currentAccordionsData: INotesSection[],
    ): INotesSection[] {
        const outputtedData = cloneDeep<INotesSection[]>(currentAccordionsData);
        const changedDataIndex = outputtedData[index].data;
        changedDataIndex[componentIndex].data = cloneDeep<Notes>(change);

        return outputtedData;
    }

    mapINotesAccordionsToINotesSections(notesAccordions: INotesAccordion[]): INotesSection[] {
        // Removing the property 'previousYearData' from all components
        return notesAccordions
            .filter((accordion) => accordion != null)
            .map((accordion) => ({
                ...accordion,
                data: accordion.data
                    .filter((component) => component != null)
                    .map(({ previousYearData, ...rest }) => rest),
            }));
    }

    addPreviousYearDataToNotesAccordionComponents(
        currentNotesSections: INotesSection[],
        previousNotesSections: INotesSection[],
    ): INotesAccordion[] {
        const previousYearSuperTableComponents = previousNotesSections
            .flatMap((accordion) => accordion.data) // Flatten the components array
            .filter((component) => component != null && component.componentName === ComponentType.SUPER_TABLE);

        const previousYearMovementScheduleComponents = previousNotesSections
            .flatMap((accordion) => accordion.data)
            .filter((component) => component != null && component.componentName === ComponentType.MOVEMENT_SCHEDULE);

        let superTableIterator = 0;
        let movementScheduleIterator = 0;

        const currentNotesAccordions: INotesAccordion[] = cloneDeep<INotesAccordion[]>(currentNotesSections);
        currentNotesAccordions.forEach((currentNotesAccordions) => {
            if (!currentNotesAccordions) {
                return;
            }
            for (const noteComponent of currentNotesAccordions.data) {
                if (!noteComponent) {
                    continue;
                }
                if (noteComponent.componentName === ComponentType.SUPER_TABLE) {

                    if (noteComponent.componentId === previousYearSuperTableComponents[superTableIterator]?.componentId) {
                        noteComponent.previousYearData = previousYearSuperTableComponents[superTableIterator]?.data;
                        superTableIterator = superTableIterator + 1;
                    }
                }

                if (noteComponent.componentName === ComponentType.MOVEMENT_SCHEDULE) {
                    if (noteComponent.componentId === previousYearMovementScheduleComponents[movementScheduleIterator]?.componentId) {
                        noteComponent.data.previousMovementScheduleComponent = previousYearMovementScheduleComponents[movementScheduleIterator]?.data;
                        movementScheduleIterator = movementScheduleIterator + 1;
                    }
                }
            }
        });
        return currentNotesAccordions;
    }

    public prepareNotesAccordionData(
        notes: NotesTypeOfNotesTemplateDTO[],
        annualReportDefaultSettings: IAnnualReportDefaultSettings,
    ): INotesSection[] {
        const accordionsData: INotesSection[] = [];
        const retrievedNotes: NotesTypeOfNotesTemplateDTO[] = cloneDeep<NotesTypeOfNotesTemplateDTO[]>(notes);

        for (const note of retrievedNotes) {
            const notesComponents = note.componentsOptions.map((component) => component.componentName);
            const accordionData: INotesSection = {
                name: note.name,
                title: note.title,
                data: this.getAccordionData(notesComponents, note.componentsOptions, annualReportDefaultSettings),
                information: note.information,
            };

            accordionsData.push(accordionData);
        }

        return accordionsData;
    }

    private getAccordionData(
        selectedNotesComponents: ComponentType[],
        noteComponentsOptions: ComponentsOptionsDTO[],
        annualReportDefaultSettings: IAnnualReportDefaultSettings,
    ): INotesComponent[] {
        const dataOfAccordion: INotesComponent[] = [];
        const componentOptionsMap: Map<
            string,
            Omit<ComponentsOptionsDTO, 'componentName'>
        > = this.notesTypesHelper.getComponentOptions(noteComponentsOptions);
        for (const component of selectedNotesComponents) {
            const data: INotesComponent = {
                componentId: componentOptionsMap.get(component).componentId,
                componentName: component,
                data: this.getAccordionDataByComponentName(
                    component,
                    componentOptionsMap.get(component).componentOptions,
                    annualReportDefaultSettings,
                ),
                hasFootnotes: false,
                isHidden: false,
            };

            dataOfAccordion.push(data);
        }

        return dataOfAccordion;
    }

    private getAccordionDataByComponentName(
        componentName: ComponentType,
        componentOptions: Options[],
        annualReportDefaultSettings: IAnnualReportDefaultSettings,
    ): Notes {
        switch (componentName) {
            case ComponentType.RICH_TEXT:
                return componentOptions as IRichText[];

            case ComponentType.EQUITY:
                const equityComponentOption = componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IEquityOptions;
                return this.prepareEquityComponentData(equityComponentOption, annualReportDefaultSettings);

            case ComponentType.FINANCIAL_LINE_ITEM:
                const financialLineItemComponentOption = componentOptions[
                    SINGLE_ELEMENT_COMPONENT_OPTIONS
                ] as IFinancialLineItemOptions;
                return this.prepareFliComponentData(financialLineItemComponentOption, annualReportDefaultSettings);

            case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                const freeTableWithAmountComponentOption = componentOptions[
                    SINGLE_ELEMENT_COMPONENT_OPTIONS
                ] as IFreeTableOptions;
                return this.prepareFreeTableWithAmountComponentData(
                    freeTableWithAmountComponentOption,
                    annualReportDefaultSettings,
                );

            case ComponentType.SELECT:
                const selectComponentOption = componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as ISelectOptions;
                return this.prepareSelectComponentData(selectComponentOption, annualReportDefaultSettings);

            case ComponentType.INPUT:
                const inputComponentOption = componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IInputOptions;
                return this.prepareInputComponentData(inputComponentOption);

            case ComponentType.MOVEMENT_SCHEDULE:
                const movementScheduleComponentOption = componentOptions[
                    SINGLE_ELEMENT_COMPONENT_OPTIONS
                ] as IMovementScheduleEditableColumnHeaders;
                return this.prepareMovementScheduleData(movementScheduleComponentOption, annualReportDefaultSettings);

            case ComponentType.SUPER_TABLE:
                const superTableComponentOption = componentOptions[
                    SINGLE_ELEMENT_COMPONENT_OPTIONS
                ] as ISuperTableOptions;
                return this.prepareSuperTableData(superTableComponentOption);
            default:
                throw new Error(` This component: ${componentName} is not supported!`);
        }
    }

    private prepareEquityComponentData(
        componentOptions: IEquityOptions,
        annualReportDefaultSettings: IAnnualReportDefaultSettings,
    ): IEquity {
        const equityData: IEquity = {
            currency: annualReportDefaultSettings.currency,
            currentDate: annualReportDefaultSettings.currentBalanceDate,
            previousDate: annualReportDefaultSettings.previousBalanceDate,
            options: componentOptions,
            rows: [],
            totalRow: null,
        };

        return equityData;
    }

    private prepareFliComponentData(
        componentOptions: IFinancialLineItemOptions,
        annualReportDefaultSettings: IAnnualReportDefaultSettings,
    ): IFinancialLineItemComponent {
        const fliData: IFinancialLineItemComponent = {
            currentBalanceYearDate: annualReportDefaultSettings.currentBalanceDate,
            previousBalanceYearDate: annualReportDefaultSettings.previousBalanceDate,
            currentIncomeYearDate: annualReportDefaultSettings.currentIncomeDate,
            previousIncomeYearDate: annualReportDefaultSettings.previousIncomeDate,
            options: componentOptions,
            flis: [],
        };

        return fliData;
    }

    private prepareFreeTableWithAmountComponentData(
        componentOptions: IFreeTableOptions,
        annualReportDefaultSettings: IAnnualReportDefaultSettings,
    ): IFreeTable {
        const freeTableWithAmountData: IFreeTable = {
            currency: annualReportDefaultSettings.currency,
            currentDate: annualReportDefaultSettings.currentBalanceDate,
            previousDate: annualReportDefaultSettings.previousBalanceDate,
            options: componentOptions,
            rows: [],
            totalRow: null,
        };

        return freeTableWithAmountData;
    }

    private prepareSelectComponentData(
        componentOptions: ISelectOptions,
        annualReportDefaultSettings: IAnnualReportDefaultSettings,
    ): INotesSelectComponent {
        const selectData: INotesSelectComponent = {
            currentYearDate: annualReportDefaultSettings.currentBalanceDate,
            previousYearDate: annualReportDefaultSettings.previousBalanceDate,
            currentYearValue: undefined,
            previousYearValue: undefined,
            options: componentOptions,
        };

        return selectData;
    }

    private prepareInputComponentData(componentOptions: IInputOptions): INotesInputComponent {
        const data: INotesInputComponent = {
            currentYearValue: null,
            previousYearValue: null,
            options: componentOptions,
        };

        return data;
    }

    private prepareSuperTableComponentData(componentOptions: ISuperTableOptions): ISuperTableComponent {
        const data: ISuperTableComponent = {
            rows: [],
            options: componentOptions,
            calculations: [],
        };
        return data;
    }

    private prepareMovementScheduleData(
        componentOptions: IMovementScheduleEditableColumnHeaders,
        annualReportDefaultSettings: IAnnualReportDefaultSettings,
    ): IMovementScheduleComponent {
        return {
            currentBalanceYearDate: annualReportDefaultSettings.currentBalanceDate,
            previousBalanceYearDate: annualReportDefaultSettings.previousBalanceDate,
            currentIncomeYearDate: annualReportDefaultSettings.currentIncomeDate,
            previousIncomeYearDate: annualReportDefaultSettings.previousIncomeDate,
            flis: [],
            headerOptions: componentOptions,
        };
    }

    public createEmptyNoteComponent(
        component: ComponentType,
        annualReportDefaultSetting: IAnnualReportDefaultSettings,
        languages: string[],
        fxEnabled = false,
    ): Notes {
        let noteComponent: Notes;
        const componentOptions = this.prepareEmptyComponentOptions(component, languages, fxEnabled);
        switch (component) {
            case ComponentType.EQUITY:
                noteComponent = this.prepareEquityComponentData(
                    componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IEquityOptions,
                    annualReportDefaultSetting,
                );
                break;

            case ComponentType.MOVEMENT_SCHEDULE:
                noteComponent = this.prepareMovementScheduleData(
                    componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IMovementScheduleEditableColumnHeaders,
                    annualReportDefaultSetting,
                );
                break;
            case ComponentType.FINANCIAL_LINE_ITEM:
                noteComponent = this.prepareFliComponentData(
                    componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IFinancialLineItemOptions,
                    annualReportDefaultSetting,
                );
                break;

            case ComponentType.RICH_TEXT:
                noteComponent = componentOptions as IRichText[];
                break;
            case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                noteComponent = this.prepareFreeTableWithAmountComponentData(
                    componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IFreeTableOptions,
                    annualReportDefaultSetting,
                );
                break;
            case ComponentType.SELECT:
                noteComponent = this.prepareSelectComponentData(
                    componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as ISelectOptions,
                    annualReportDefaultSetting,
                );
                break;
            case ComponentType.INPUT:
                noteComponent = this.prepareInputComponentData(
                    componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IInputOptions,
                );
                break;

            case ComponentType.SUPER_TABLE:
                noteComponent = this.prepareSuperTableComponentData(
                    componentOptions[SINGLE_ELEMENT_COMPONENT_OPTIONS] as ISuperTableOptions,
                );
                break;
            default:
                break;
        }

        return noteComponent;
    }

    private prepareEmptyComponentOptions(component: ComponentType, languages: string[], fxEnabled = false): Options[] {
        let options: Options[];
        switch (component) {
            case ComponentType.EQUITY:
                {
                    const equityOptions: IEquityOptions = {
                        textTitle: this.prepareEmptyDynamicText(languages),
                        currentValueTitle1: this.prepareEmptyDynamicText(languages),
                        currentValueTitle2: this.prepareEmptyDynamicText(languages),
                        previousValueTitle1: this.prepareEmptyDynamicText(languages),
                        previousValueTitle2: this.prepareEmptyDynamicText(languages),
                        totalRow: true,
                    };

                    options = [equityOptions];
                }

                break;
            case ComponentType.MOVEMENT_SCHEDULE:
                {
                    const movementScheduleOptions = cloneDeep<IMovementScheduleEditableColumnHeaders>(
                        this.movementScheduleHelper.getInitialHeaderOptions(),
                    );

                    options = [movementScheduleOptions];
                }

                break;
            case ComponentType.FINANCIAL_LINE_ITEM:
                {
                    const fliComponentOptions: IFinancialLineItemOptions = {
                        text: this.prepareEmptyDynamicText(languages),
                    };

                    options = [fliComponentOptions];
                }

                break;
            case ComponentType.RICH_TEXT:
                {
                    const richTextOptions: IRichText[] = this.prepareRichTextOptions(languages);
                    options = richTextOptions;
                }

                break;

            case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                {
                    const freeTableWithAmount: IFreeTableOptions = {
                        totalRow: true,
                    };
                    options = [freeTableWithAmount];
                }

                break;
            case ComponentType.SELECT:
                {
                    const emptyArrayOptionsOfTwoValues = [
                        this.prepareEmptyDynamicText(languages),
                        this.prepareEmptyDynamicText(languages),
                    ];
                    const select: ISelectOptions = {
                        text: this.prepareEmptyDynamicText(languages),
                        selectionOption: emptyArrayOptionsOfTwoValues,
                    };
                    options = [select];
                }

                break;

            case ComponentType.INPUT:
                {
                    const inputOptions: IInputOptions = {
                        text: this.prepareEmptyDynamicText(languages),
                        dataType: IInputDataType.TEXT,
                        numberOfDecimalPlaces: 1,
                        compareYears: false,
                        useAsParameter: false,
                    };
                    options = [inputOptions];
                }

                break;
            case ComponentType.SUPER_TABLE:
                {
                    const superTableOptions: ISuperTableOptions = {
                        title: this.prepareEmptyDynamicText(languages),
                        xAxisConfig: [
                            {
                                columnId: SuperTableColumnType.CURRENT_YEAR,
                                type: SuperTableColumnType.CURRENT_YEAR,
                                belongingYear: SuperTableColumnBelongingYear.CURRENT_YEAR,
                            },
                            {
                                columnId: SuperTableColumnType.PREVIOUS_YEAR,
                                type: SuperTableColumnType.PREVIOUS_YEAR,
                                belongingYear: SuperTableColumnBelongingYear.PREVIOUS_YEAR,
                            },
                        ],
                        segments: [],
                        compareYears: true,
                        hideCurrentYear: false,
                        showFiscalYear: false,
                        transpose: false,
                        showPreviousYear: false,
                        // FX (REV-21512): a Functional-Currency report labels its columns by default.
                        showCurrency: fxEnabled,
                    };
                    options = [superTableOptions];
                }

                break;
            default:
                break;
        }

        return options;
    }

    private prepareEmptyDynamicText(languages: string[]): IDynamicKeyValue {
        const dynamicKeyValue: IDynamicKeyValue = {};
        for (const language of languages) {
            dynamicKeyValue[language] = EMPTY_STRING;
        }
        return dynamicKeyValue;
    }

    private prepareRichTextOptions(languages: string[]): IRichText[] {
        const richText: IRichText[] = [];
        for (const language of languages) {
            richText.push({
                language,
                text: EMPTY_STRING,
            });
        }

        return richText;
    }
    private prepareSuperTableData(componentOptions: ISuperTableOptions): ISuperTableComponent {
        return {
            rows: [],
            options: componentOptions,
            calculations: [],
        };
    }

    copyNotesStructureFromAnotherAnnualReport(
        annualReportToCopyNotesStructureFrom: IAnnualReport,
        reportLocale: string,
    ): INotesSection[] {
        const copiedAnnualReportAccordions = cloneDeep(
            annualReportToCopyNotesStructureFrom.notes,
        ) as IAnnualReportAccordionsData;

        // We want to copy the notes structure and components settings
        // but not the components selected values and flis
        // Except the rich text we copy it as it is.
        const accordions: INotesSection[] = [];
        copiedAnnualReportAccordions?.accordions.forEach((accordion) => {
            if (!accordion) {
                return;
            }
            const dataToBeCopied: INotesComponent[] = [];
            accordion.data.forEach((component) => {
                if (!component) {
                    return;
                }
                switch (component.componentName) {
                    case ComponentType.RICH_TEXT: {
                        dataToBeCopied.push(this.copyRichTextComponentStructure(component, reportLocale));
                        break;
                    }
                    case ComponentType.INPUT:
                        dataToBeCopied.push(this.copySimpleInputComponentStructure(component));
                        break;
                    case ComponentType.SUPER_TABLE:
                        dataToBeCopied.push(this.copySuperTableComponentStructure(component));
                        break;
                    case ComponentType.SELECT:
                        dataToBeCopied.push(this.copySelectComponentStructure(component));
                        break;
                    case ComponentType.MOVEMENT_SCHEDULE:
                        dataToBeCopied.push(this.copyMovementScheduleComponentStructure(component));
                        break;
                    case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                        dataToBeCopied.push(this.copyFreeTableWithAmountsComponentStructure(component));
                        break;
                    case ComponentType.FINANCIAL_LINE_ITEM:
                        dataToBeCopied.push(this.copyFliComponentStructure(component));
                        break;
                    case ComponentType.EQUITY:
                        dataToBeCopied.push(this.copyEquityComponentStructure(component));
                        break;
                    default:
                        dataToBeCopied.push({ ...component, referenceFlis: [] });
                        break;
                }
            });
            accordion.data = cloneDeep(dataToBeCopied);

            accordions.push(accordion);
        });

        return accordions;
    }

    copyRichTextComponentStructure(component: INotesComponent, reportLocale: string): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: [],
            addNumbering: false,
            isHidden: false,
            data: [
                {
                    language: reportLocale,
                    text: '',
                } as IRichText,
            ],
        };
    }

    copySimpleInputComponentStructure(component: INotesComponent): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: [],
            addNumbering: false,
            isHidden: false,
            data: {
                ...component.data,
                currentYearValue: null,
                previousYearValue: null,
            },
        };
    }

    copySuperTableComponentStructure(component: INotesComponent): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: [],
            addNumbering: false,
            isHidden: false,
            data: {
                ...component.data,
                calculations: [],
                rows: [],
                referenceFlis: [],
            },
        };
    }

    copySelectComponentStructure(component: INotesComponent): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: [],
            addNumbering: false,
            isHidden: false,
            data: {
                ...component.data,
                previousYearValue: '',
                currentYearValue: '',
                referenceFlis: [],
            },
        };
    }

    copyMovementScheduleComponentStructure(component: INotesComponent): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: [],
            addNumbering: false,
            isHidden: false,
            data: {
                ...component.data,
                flis: [],
            },
        };
    }

    copyFreeTableWithAmountsComponentStructure(component: INotesComponent): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: [],
            addNumbering: false,
            isHidden: false,
            data: {
                ...component.data,
                rows: [
                    {
                        text: '',
                        currentValue: null,
                        previousValue: null,
                    },
                ],
                totalRow: {
                    text: '',
                    currentValue: 0,
                    previousValue: 0,
                },
                referenceFlis: [],
            },
        };
    }

    copyFliComponentStructure(component: INotesComponent): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: [],
            addNumbering: false,
            isHidden: false,
            data: {
                ...component.data,
                flis: [],
            },
        };
    }

    copyEquityComponentStructure(component: INotesComponent): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: [],
            addNumbering: false,
            isHidden: false,
            data: {
                ...component.data,
                rows: [
                    {
                        text: '',
                        currentValue1: '',
                        previousValue1: '',
                        currentValue2: '',
                        previousValue2: '',
                    },
                ],
                totalRow: {
                    text: '',
                    currentValue1: 0,
                    previousValue1: 0,
                    currentValue2: 0,
                    previousValue2: 0,
                },
                referenceFlis: [],
            },
        };
    }

    copyNotesStructureFromAnotherAnnualReportIncludingData(
        annualReportToCopyNotesStructureFrom: IAnnualReport,
        totalText: string,
        balanceMappingTemplate: IAccountMappingTemplate,
        incomeMappingTemplate: IAccountMappingTemplate,
        reportLocale: string,
        includeReferencesAndNumbering: boolean,
    ): INotesSection[] {
        const copiedAnnualReportAccordions = cloneDeep(
            annualReportToCopyNotesStructureFrom.notes,
        ) as IAnnualReportAccordionsData;

        const balanceFlis = getAllFlis(balanceMappingTemplate.group);
        const incomeFlis = getAllFlis(incomeMappingTemplate.group);
        const allFlis = balanceFlis.concat(incomeFlis);

        // We want to copy the notes structure and components settings
        // but not the components selected values and flis
        // Except the rich text we copy it as it is.

        const accordions: INotesSection[] = [];
        copiedAnnualReportAccordions?.accordions.forEach((accordion) => {
            if (!accordion) {
                return;
            }
            const dataToBeCopied: INotesComponent[] = [];
            accordion.data.forEach((component) => {
                if (!component) {
                    return;
                }
                switch (component.componentName) {
                    case ComponentType.RICH_TEXT: {
                        dataToBeCopied.push(
                            this.copyRichTextComponentStructureWithData(
                                component,
                                allFlis,
                                includeReferencesAndNumbering,
                            ),
                        );
                        break;
                    }
                    case ComponentType.INPUT:
                        dataToBeCopied.push(
                            this.copySimpleInputComponentStructureWithData(
                                component,
                                allFlis,
                                includeReferencesAndNumbering,
                            ),
                        );
                        break;
                    case ComponentType.SUPER_TABLE: {
                        dataToBeCopied.push(
                            this.copySuperTableComponentStructureWithData(
                                component,
                                allFlis,
                                balanceMappingTemplate,
                                incomeMappingTemplate,
                                includeReferencesAndNumbering,
                            ),
                        );
                        break;
                    }

                    case ComponentType.SELECT:
                        dataToBeCopied.push(
                            this.copySelectComponentStructureWithData(
                                component,
                                allFlis,
                                includeReferencesAndNumbering,
                            ),
                        );
                        break;
                    case ComponentType.MOVEMENT_SCHEDULE:
                        dataToBeCopied.push(
                            this.copyMovementScheduleComponentStructureWithData(
                                component,
                                allFlis,
                                balanceFlis,
                                incomeFlis,
                                reportLocale,
                                includeReferencesAndNumbering,
                            ),
                        );
                        break;
                    case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                        dataToBeCopied.push(
                            this.copyFreeTableWithAmountsComponentStructureWithData(
                                component,
                                allFlis,
                                includeReferencesAndNumbering,
                            ),
                        );
                        break;
                    case ComponentType.FINANCIAL_LINE_ITEM:
                        dataToBeCopied.push(
                            this.copyFliComponentStructureWithData(
                                component,
                                allFlis,
                                balanceFlis,
                                incomeFlis,
                                includeReferencesAndNumbering,
                            ),
                        );
                        break;
                    case ComponentType.EQUITY:
                        dataToBeCopied.push(
                            this.copyEquityComponentStructureWithData(
                                component,
                                totalText,
                                allFlis,
                                includeReferencesAndNumbering,
                            ),
                        );
                        break;
                    default:
                        dataToBeCopied.push({ ...component, referenceFlis: [] });
                        break;
                }
            });
            accordion.data = cloneDeep(dataToBeCopied);

            accordions.push({ ...accordion, referenceFlis: findFlis(accordion.referenceFlis || [], allFlis) });
        });

        return accordions;
    }

    copyRichTextComponentStructureWithData(
        component: INotesComponent,
        allFlis: IAccountMappingTemplateGroupItemFLI[],
        includeReferencesAndNumbering: boolean,
    ): INotesComponent {
        return {
            ...component,
            addNumbering: includeReferencesAndNumbering ? component.addNumbering : undefined,
            referenceFlis: includeReferencesAndNumbering ? findFlis(component.referenceFlis || [], allFlis) : undefined,
            isHidden: false,
        };
    }

    copySimpleInputComponentStructureWithData(
        component: INotesComponent,
        allFlis: IAccountMappingTemplateGroupItemFLI[],
        includeReferencesAndNumbering: boolean,
    ): INotesComponent {
        return {
            ...component,
            addNumbering: includeReferencesAndNumbering ? component.addNumbering : undefined,
            referenceFlis: includeReferencesAndNumbering ? findFlis(component.referenceFlis || [], allFlis) : undefined,
            isHidden: false,
            data: {
                ...component.data,
            },
        };
    }

    copySuperTableComponentStructureWithData(
        component: INotesComponent,
        allFlis: IAccountMappingTemplateGroupItemFLI[],
        balanceMappingTemplate: IAccountMappingTemplate,
        incomeMappingTemplate: IAccountMappingTemplate,
        includeReferencesAndNumbering: boolean,
    ): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: includeReferencesAndNumbering ? findFlis(component.referenceFlis || [], allFlis) : undefined,
            addNumbering: includeReferencesAndNumbering ? component.addNumbering : undefined,
            isHidden: false,
            data: {
                ...component.data,
                addNumbering: includeReferencesAndNumbering ? component.data.addNumbering : undefined,
                referenceFlis: includeReferencesAndNumbering
                    ? findFlis(component.data.referenceFlis || [], allFlis)
                    : undefined,
                rows: getSuperTableCopiedRows(
                    component.data.rows,
                    component.data.options.xAxisConfig,
                    balanceMappingTemplate,
                    incomeMappingTemplate,
                ),
            } as ISuperTableComponent,
        };
    }

    copySelectComponentStructureWithData(
        component: INotesComponent,
        allFlis: IAccountMappingTemplateGroupItemFLI[],
        includeReferencesAndNumbering: boolean,
    ): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: includeReferencesAndNumbering ? findFlis(component.referenceFlis || [], allFlis) : undefined,
            addNumbering: includeReferencesAndNumbering ? component.addNumbering : undefined,
            isHidden: false,
            data: {
                ...component.data,
                referenceFlis: includeReferencesAndNumbering
                    ? findFlis(component.data.referenceFlis || [], allFlis)
                    : undefined,
                addNumbering: includeReferencesAndNumbering ? component.data.addNumbering : undefined,
            } as INotesSelectComponent,
        };
    }

    copyMovementScheduleComponentStructureWithData(
        component: INotesComponent,
        allFlis: IAccountMappingTemplateGroupItemFLI[],
        balanceFlis: IAccountMappingTemplateGroupItemFLI[],
        incomeFlis: IAccountMappingTemplateGroupItemFLI[],
        reportLocale: string,
        includeReferencesAndNumbering: boolean,
    ): INotesComponent {

        // Destructure to exclude previousMovementScheduleComponent from being copied
        const { previousMovementScheduleComponent, ...dataWithoutPrevious } = component.data as IMovementScheduleComponent;

        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: includeReferencesAndNumbering ? findFlis(component.referenceFlis || [], allFlis) : undefined,
            addNumbering: includeReferencesAndNumbering ? component.addNumbering : undefined,
            isHidden: false,
            data: {
                ...dataWithoutPrevious,
                flis: mapFlisForMovement(
                    component.data.flis || [],
                    allFlis,
                    balanceFlis,
                    incomeFlis,
                    component.data,
                    reportLocale,
                ),
            } as IMovementScheduleComponent,
        };
    }

    copyFreeTableWithAmountsComponentStructureWithData(
        component: INotesComponent,
        allFlis: IAccountMappingTemplateGroupItemFLI[],

        includeReferencesAndNumbering: boolean,
    ): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: includeReferencesAndNumbering ? findFlis(component.referenceFlis || [], allFlis) : undefined,
            addNumbering: includeReferencesAndNumbering ? component.addNumbering : undefined,
            isHidden: false,
            data: {
                ...component.data,
                addNumbering: includeReferencesAndNumbering ? component.data.addNumbering : undefined,
                referenceFlis: includeReferencesAndNumbering
                    ? findFlis(component.data.referenceFlis || [], allFlis)
                    : undefined,
            } as IFreeTable,
        };
    }

    copyFliComponentStructureWithData(
        component: INotesComponent,
        allFlis: IAccountMappingTemplateGroupItemFLI[],
        balanceFlis: IAccountMappingTemplateGroupItemFLI[],
        incomeFlis: IAccountMappingTemplateGroupItemFLI[],
        includeReferencesAndNumbering: boolean,
    ): INotesComponent {
        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: includeReferencesAndNumbering ? findFlis(component.referenceFlis || [], allFlis) : undefined,
            addNumbering: includeReferencesAndNumbering ? component.addNumbering : undefined,
            isHidden: false,
            data: {
                ...component.data,
                flis: mapFlis(component.data.flis || [], allFlis, balanceFlis, incomeFlis),
            } as IFinancialLineItemComponent,
        };
    }

    copyEquityComponentStructureWithData(
        component: INotesComponent,
        totalText: string,
        allFlis: IAccountMappingTemplateGroupItemFLI[],
        includeReferencesAndNumbering: boolean,
    ): INotesComponent {
        const [rows, totalRow] = getTotalAndRows(component, totalText);

        return {
            ...component,
            componentId: NotesHelper.generateComponentId(component.componentName),
            referenceFlis: includeReferencesAndNumbering ? findFlis(component.referenceFlis || [], allFlis) : undefined,
            addNumbering: includeReferencesAndNumbering ? component.addNumbering : undefined,
            isHidden: false,
            data: {
                ...component.data,
                rows: rows,
                totalRow: totalRow,
                referenceFlis: findFlis(component.data.referenceFlis || [], allFlis),
                addNumbering: includeReferencesAndNumbering ? component.data.addNumbering : undefined,
            } as IEquity,
        };
    }
}
